/**
 * Generic URL/query helper (e.g. auth redirects, IdP URLs).
 */

export type AppendOrSetSearchParamValue = string | number | boolean;

/**
 * When `document` is unavailable (`window` missing or no `location.origin`),
 * relative `href` strings still need a parse base for `new URL(href, base)`.
 * Uses `.invalid` (RFC 6761 — guaranteed non-routable) so this is never mistaken
 * for “bind to localhost”; callers only see pathname/search/hash for non-absolute URLs.
 */
const RELATIVE_RESOLUTION_BASE = 'http://relative-resolution.invalid/';

function appendEncodedQueryBeforeHash(
  href: string,
  encodedPair: string
): string {
  const hashIdx = href.indexOf('#');
  const beforeHash = hashIdx >= 0 ? href.slice(0, hashIdx) : href;
  const hash = hashIdx >= 0 ? href.slice(hashIdx) : '';
  const sep = beforeHash.includes('?') ? '&' : '?';
  return `${beforeHash}${sep}${encodedPair}${hash}`;
}

/**
 * Sets or replaces a single search parameter. Accepts absolute URLs or paths
 * (e.g. `/login`). Uses the URL parser when valid; fallback keeps queries
 * **before** the `#fragment`.
 */
export function appendOrSetSearchParam(
  href: string,
  key: string,
  value: AppendOrSetSearchParamValue
): string {
  if (!href) return href;

  const encodedPair = `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;

  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : RELATIVE_RESOLUTION_BASE;
    const isAbsolute =
      href.startsWith('http://') || href.startsWith('https://');
    const url = isAbsolute ? new URL(href) : new URL(href, base);
    url.searchParams.set(key, String(value));
    if (!isAbsolute) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return appendEncodedQueryBeforeHash(href, encodedPair);
  }
}
